import React from 'react';
import type { ApolloClient } from '@apollo/client';
import { ApolloProvider } from '@apollo/client';
import dynamic from 'next/dynamic';
import { IntlProvider } from 'react-intl';
import type { Tenant } from '@aurora/shared-types/tenant';
import IntlHelper from '@aurora/shared-utils/helpers/i18n/IntlHelper';
import { DEFAULT_LANGUAGE } from '@aurora/shared-utils/helpers/i18n/defaultLanguage';
import TenantContext from '../../context/TenantContext';
import CachedThemeContextProvider from '../../context/ThemeContext/CachedThemeContextProvider/CachedThemeContextProvider';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import PageError from './PageError';
const StaticPageError = dynamic(() => import('../StaticPageError/StaticPageError'));

interface Props {
  /**
   * The status code.
   */
  statusCode?: number;
  /**
   * The page text.
   */
  title?: string;
  /**
   * The tenant.
   */
  tenant: Tenant;
  /**
   * The apollo app config
   */
  apolloClient: ApolloClient<unknown>;
}

/**
 * A component that renders information about a page error. This component should only be used at the
 * page level, and not inside individual components. This component wraps the `PageErrorInternal` component
 * with an `ErrorBoundary`, so that it can fall back a static page error component, called StaticPageError,
 * that has no reliance on any application state or logic.
 *
 * @author Corey Aing, Adam Ayres
 */
const PageErrorBoundary: React.FC<React.PropsWithChildren<Props>> = ({
  statusCode = 404,
  title,
  apolloClient,
  tenant
}): React.ReactElement => {
  const { locale } = IntlHelper.getResolvedOptions();
  return (
    <ErrorBoundary fallbackComponent={() => <StaticPageError />}>
      <TenantContext.Provider key={tenant?.id} value={tenant}>
        <ApolloProvider client={apolloClient}>
          <IntlProvider defaultLocale={DEFAULT_LANGUAGE} locale={locale}>
            <CachedThemeContextProvider>
              <PageError statusCode={statusCode} title={title} />
            </CachedThemeContextProvider>
          </IntlProvider>
        </ApolloProvider>
      </TenantContext.Provider>
    </ErrorBoundary>
  );
};

export default PageErrorBoundary;
